import React, { PropsWithChildren, ReactNode } from 'react';
import classNames from 'classnames';

import Text from '../text/Text';
import TableBody from './TableBody';
import TableHead from './TableHead';
import TableHeading from './TableHeading';
import TableRow from './TableRow';
import { HeaderExtensionType, Row } from './component/types';
import HeaderExtension from './component/HeaderExtension';
import Pagination, { PaginationProps } from '../pagination/Pagination';
import Loading from './component/Loading';
import { DownloadButtonsProps } from '../download-buttons/DownloadButtons';

interface TableSecondaryColumn {
  id: string | number;
  name: string | ReactNode;
  colSpan?: number;
  rowSpan?: number;
  hide?: boolean;
}

interface TableSecondaryProps<T> {
  columns: TableSecondaryColumn[];
  rows?: Row[];
  columnSecondary?: TableSecondaryColumn[];
  border?: boolean;
  headerExtension?: HeaderExtensionType;
  pagination?: PaginationProps;
  loading?: boolean;
  loadingItemCount?: number;
  responsive?: boolean;
  tableType?: 'primary' | 'danger' | 'success' | 'warning' | 'info';
  download?: DownloadButtonsProps<T>;
  loadingHeight?: string;
}

export function ColumnName({ item }: { item: TableSecondaryColumn }) {
  return (
    <div className="d-flex align-items-center gap-2">
      <Text color="subtitle1" size="md" weight="medium">
        {item.name}
      </Text>
    </div>
  );
}

export function TableSecondary<T>({
  columns = [],
  rows = [],
  columnSecondary = [],
  children,
  border,
  headerExtension,
  pagination,
  loading,
  loadingItemCount,
  responsive = true,
  tableType,
  download,
  loadingHeight,
}: PropsWithChildren<TableSecondaryProps<T>>) {
  return (
    <div
      className={classNames('border bg-white', {
        'rounded-6': columns.length > 0,
        'overflow-hidden': responsive,
        [`border-${tableType}-semi-midlight`]: tableType,
      })}
    >
      <div
        className={classNames({
          'overflow-x-auto overflow-y-hidden': responsive,
        })}
      >
        {headerExtension ? (
          <HeaderExtension
            headerExtension={headerExtension}
            fileName={download?.fileName}
            columns={download?.columns || columns}
            rows={download?.rows || rows}
            onClickDownload={download?.onClickDownload}
            isLandscapeOrientation={download?.isLandscapeOrientation}
            downloadLoading={download?.downloadLoading}
          />
        ) : null}

        <table
          className={classNames('table', {
            'table-border': border,
            [`table-${tableType}`]: tableType,
          })}
        >
          {columns.length > 0 ? (
            <TableHead>
              <TableRow>
                {columns.map((item) =>
                  !item?.hide ? (
                    <TableHeading
                      colSpan={item.colSpan}
                      rowSpan={item.rowSpan}
                      key={item.id}
                    >
                      <ColumnName item={item} />
                    </TableHeading>
                  ) : null,
                )}
              </TableRow>

              {columnSecondary.length > 0 ? (
                <TableRow>
                  {columnSecondary.map((item) => (
                    <TableHeading className="bg-light" key={item.id}>
                      <ColumnName item={item} />
                    </TableHeading>
                  ))}
                </TableRow>
              ) : null}
            </TableHead>
          ) : null}

          <TableBody>
            {!loading ? children : null}
            {loading ? (
              <Loading
                loadingItemCount={loadingItemCount}
                columns={columns}
                height={loadingHeight}
              />
            ) : null}
          </TableBody>
        </table>

        {pagination && pagination?.totalPage > 1 ? (
          <div className="bg-white p-8 rounded-bottom-4 border-top">
            <Pagination {...pagination} />
          </div>
        ) : null}
      </div>
    </div>
  );
}

export default TableSecondary;
